import { supabase } from '../lib/supabase';

/**
 * One row of operations.operator_messages — the preset warnings/instructions an
 * operator can send to the visitor in the headset ("Niet rennen"). The agent
 * plays the pre-rendered clip {key}_{lang}_v{clip_version}.wav from the
 * operator-messages bucket; adding a message is one DB row + two clip uploads,
 * no release. See RealityAgent/docs/visitor-message-contract.md.
 */
export interface MessageText {
  text: string;
  clip_version: number;
}

export interface OperatorMessage {
  key: string;
  label: string;
  /** Legacy mirror of the nl/en rows, kept so pre-multilingual agents still work. */
  text_nl: string;
  text_en: string;
  clip_version: number;
  sort: number;
  active: boolean;
  /**
   * Per-language text + clip version, keyed by public.languages.short_name.
   * Holds the enabled languages plus English, which is always present because
   * the agent falls back to it per message. Empty when the multilingual
   * migration has not been applied yet; the agent then uses the legacy fields.
   */
  texts: Record<string, MessageText>;
  /**
   * Headset dialog heading per language. Per language, not per message: it is a
   * generic heading. A language without one leaves the PC's configured
   * visitor_messages.headset_dialog_title in charge.
   */
  titles: Record<string, string>;
}

/**
 * Hardcoded fallback matching the migration seed, so the feature keeps working
 * before the operator_messages migration is applied (or if the query fails).
 */
const DEFAULT_MESSAGES: OperatorMessage[] = [
  { key: 'no_running', label: 'Niet rennen', text_nl: 'Niet rennen alsjeblieft.', text_en: "Please don't run.", clip_version: 1, sort: 10, active: true, titles: {}, texts: { nl: { text: 'Niet rennen alsjeblieft.', clip_version: 1 }, en: { text: "Please don't run.", clip_version: 1 } } },
  { key: 'no_screaming', label: 'Niet schreeuwen', text_nl: 'Niet schreeuwen alsjeblieft.', text_en: "Please don't scream.", clip_version: 1, sort: 20, active: true, titles: {}, texts: { nl: { text: 'Niet schreeuwen alsjeblieft.', clip_version: 1 }, en: { text: "Please don't scream.", clip_version: 1 } } },
  { key: 'stay_in_area', label: 'Blijf in speelgebied', text_nl: 'Blijf binnen het speelgebied alsjeblieft.', text_en: 'Please stay inside the play area.', clip_version: 1, sort: 30, active: true, titles: {}, texts: { nl: { text: 'Blijf binnen het speelgebied alsjeblieft.', clip_version: 1 }, en: { text: 'Please stay inside the play area.', clip_version: 1 } } },
  { key: 'staff_incoming', label: 'Medewerker komt eraan', text_nl: 'Er komt een medewerker naar je toe.', text_en: 'A staff member is coming to help you.', clip_version: 1, sort: 40, active: true, titles: {}, texts: { nl: { text: 'Er komt een medewerker naar je toe.', clip_version: 1 }, en: { text: 'A staff member is coming to help you.', clip_version: 1 } } },
];

/**
 * Fetch-once cache (same pattern as issueCategories). Only successful,
 * non-empty results are cached so a pre-migration deploy retries next time.
 */
let messagesCache: OperatorMessage[] | null = null;

export async function getOperatorMessages(): Promise<OperatorMessage[]> {
  if (messagesCache) {
    return messagesCache;
  }

  try {
    const { data, error } = await supabase
      .schema('operations')
      .from('operator_messages')
      .select('key, label, text_nl, text_en, clip_version, sort, active')
      .eq('active', true)
      .order('sort', { ascending: true });

    if (error || !data || data.length === 0) {
      if (error) {
        console.error('Error fetching operator messages (using defaults):', error);
      }
      return DEFAULT_MESSAGES;
    }

    const { texts: byKey, titles } = await fetchMessageTexts();
    messagesCache = (data as OperatorMessage[]).map((m) => ({
      ...m,
      titles,
      // Fall back to the legacy columns so a pre-migration deploy still sends a
      // full map rather than nothing (both languages, one shared version).
      texts: byKey[m.key] ?? {
        nl: { text: m.text_nl, clip_version: m.clip_version },
        en: { text: m.text_en, clip_version: m.clip_version },
      },
    }));
    return messagesCache;
  } catch (err) {
    console.error('Exception fetching operator messages (using defaults):', err);
    return DEFAULT_MESSAGES;
  }
}

/**
 * Per-language texts from operations.v_operator_message_texts, grouped by message
 * key. Only enabled languages are offered, but English is always included: the
 * agent falls back to it per message, so a language that is switched on while
 * some messages are still unvoiced stays usable. A missing view (pre-migration)
 * is not an error, it just means the legacy columns are still the whole truth.
 */
async function fetchMessageTexts(): Promise<{
  texts: Record<string, Record<string, MessageText>>;
  titles: Record<string, string>;
}> {
  const { data, error } = await supabase
    .schema('operations')
    .from('v_operator_message_texts')
    .select('key, lang, text, clip_version, is_enabled, dialog_title');

  if (error || !data) {
    if (error) {
      console.warn('No per-language message texts (using the legacy columns):', error.message);
    }
    return { texts: {}, titles: {} };
  }

  const texts: Record<string, Record<string, MessageText>> = {};
  const titles: Record<string, string> = {};
  for (const row of data as Array<{
    key: string;
    lang: string;
    text: string;
    clip_version: number;
    is_enabled: boolean;
    dialog_title: string | null;
  }>) {
    const lang = (row.lang || '').toLowerCase();
    if (!lang || (!row.is_enabled && lang !== 'en')) continue;
    // The title repeats on every row of a language; the first one is enough.
    if (row.dialog_title?.trim() && !titles[lang]) titles[lang] = row.dialog_title.trim();
    if (!row.text || row.clip_version < 1) continue;
    (texts[row.key] ??= {})[lang] = { text: row.text, clip_version: row.clip_version };
  }
  return { texts, titles };
}

/** Clear the cache (e.g. after an admin edits messages). */
export function clearOperatorMessagesCache(): void {
  messagesCache = null;
}
